/* © NXAPP Limited */
/**
 * TM — Telemarketing. The calling arm of Individual Outreach.
 *
 * Left: the job queue (call-backs due first, then oldest email first).
 * Middle: the CRM record — who they are, what they were sent, status, work log.
 * Right: the caller's rail — AI company profile, the first-call script, the FAQ.
 *
 * Leads only appear here after Touch 1 has SENT and only while the contact is
 * still `active` on the email side — a reply removes them from the queue with
 * no sync step (the query joins outreach_contacts.status).
 */
import { useState, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { authedFetch } from '@/lib/authedFetch'
import { describeError } from '@/lib/describeError'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/ui/EmptyState'
import TrendStrip from '@/components/dashboard/TrendStrip'
import RowSplit from '@/components/dashboard/RowSplit'
import { dayKeys, bucketCount, emptySplit, addToSplit, londonDayKey, type Split } from '@/lib/trend'
import {
  Phone, PhoneCall, CalendarClock, CalendarCheck, Ban, Building2, Mail,
  Sparkles, RefreshCw, ChevronLeft, FileText, HelpCircle, StickyNote, Clock, Search, Plus,
  Upload, ChevronDown, ChevronRight, Pencil, Trash2, Check, X,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────
type TmStatus = 'to_call' | 'callback' | 'meeting_booked' | 'not_interested' | 'dnc'

interface TmLead {
  id: string
  campaign_id: string
  outreach_contact_id: string
  full_name: string | null
  company: string | null
  email: string
  source_touch_no: number
  email_sent_at: string | null
  next_email_at: string | null
  phone: string | null
  phone_source: string | null
  phone_confidence: 'high' | 'medium' | 'low' | null
  website: string | null
  company_summary: string | null
  enrichment_status: 'pending' | 'running' | 'done' | 'failed' | 'no_data'
  status: TmStatus
  callback_at: string | null
  meeting_at: string | null
  last_call_at: string | null
  call_count: number
  created_at: string
}

interface TmNote {
  id: string
  lead_id: string
  body: string
  outcome: TmStatus | null
  is_call: boolean
  created_at: string
}

interface SentEmail {
  id: string
  touch_no: number
  subject: string
  body: string
  status: string
  sent_at: string | null
  scheduled_for: string | null
}

interface TmConfig { script?: string; faq?: { q: string; a: string }[] }

// ── Status vocabulary — the words the caller actually uses ────────────────────
const STATUS: Record<TmStatus, { label: string; cls: string; pill: string }> = {
  to_call:         { label: 'To Call',                    cls: 'text-teal-700',    pill: 'bg-teal-50 text-teal-700 border-teal-200' },
  callback:        { label: 'Callback',                   cls: 'text-amber-700',   pill: 'bg-amber-50 text-amber-700 border-amber-200' },
  meeting_booked:  { label: 'Meeting Booked',             cls: 'text-green-700',   pill: 'bg-green-50 text-green-700 border-green-200' },
  not_interested:  { label: 'Not Interested',             cls: 'text-gray-600',    pill: 'bg-gray-100 text-gray-600 border-gray-200' },
  dnc:             { label: 'Do Not Contact Me Again',    cls: 'text-red-700',     pill: 'bg-red-50 text-red-700 border-red-200' },
}
const STATUS_ORDER: TmStatus[] = ['to_call', 'callback', 'meeting_booked', 'not_interested', 'dnc']

/** A status that takes the lead out of the queue for good. */
const CLOSED: TmStatus[] = ['meeting_booked', 'not_interested', 'dnc']

const ukStamp = (iso: string) => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
}).format(new Date(iso)).replace(',', '')

const ukDate = (iso: string) => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', day: 'numeric', month: 'short',
}).format(new Date(iso))

/** "3 days ago" — how stale the email is, which shapes the opening line. */
function daysAgo(iso: string): string {
  const d = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)
  if (d <= 0) return 'today'
  if (d === 1) return 'yesterday'
  return `${d} days ago`
}

// ── Email preview — reproduce what was ACTUALLY sent ──────────────────────────
// Outreach drafts store the raw template ({{firstName}}, snippet {{tags}}, no
// signature); merging happens at send. To show the real email in TM we merge
// the same way IndividualContent does, using the contact + the SOURCE campaign's
// snippets and signature.
interface Preset { tag: string; text?: string }
interface MergeContact { first_name: string | null; last_name: string | null; firm: string | null }
const linkifyPreview = (t: string) => (t ?? '')
  .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" style="color:#2563eb;">$1</a>')
  .replace(/\n/g, '<br />')
function unwrapDivPreview(html: string): string {
  const s = (html ?? '').trim()
  const m = s.match(/^<div\b[^>]*>([\s\S]*)<\/div>$/i)
  return m ? m[1].trim() : s
}
/** Expand snippet {{tag}} then personalisation tags. `linkify` off for subjects. */
function fillTags(text: string, c: MergeContact | null | undefined, presets: Preset[], linkify = true): string {
  let out = text || ''
  for (const p of presets) if (p.tag) out = out.replace(new RegExp(`\\{\\{\\s*${p.tag.replace(/[^a-z0-9_]/gi, '')}\\s*\\}\\}`, 'gi'), linkify ? linkifyPreview(p.text ?? '') : (p.text ?? ''))
  return out
    .replace(/\{\{\s*firstName\s*\}\}/gi, c?.first_name || 'there')
    .replace(/\{\{\s*lastName\s*\}\}/gi, c?.last_name || '')
    .replace(/\{\{\s*(company|firm)\s*\}\}/gi, c?.firm || 'your firm')
}
/** Body + signature, exactly as it sent: fill, trim, one blank line, signature. */
function renderSentPreview(body: string, c: MergeContact | null | undefined, presets: Preset[], signature: string): string {
  const trail = /(?:\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<div>\s*<\/div>|<p><br\s*\/?><\/p>)+$/i
  const lead = /^(?:\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<div>\s*<\/div>|<p><br\s*\/?><\/p>)+/i
  const out = unwrapDivPreview(fillTags(body, c, presets)).replace(trail, '')
  const sig = unwrapDivPreview(fillTags(signature, c, presets)).replace(lead, '').replace(trail, '')
  return sig ? `${out}<br /><br />${sig}` : out
}

export default function TMPage({ campaignId: propCampaignId, embedded }: { campaignId?: string; embedded?: boolean } = {}) {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [campaignId, setCampaignId] = useState<string>(propCampaignId ?? '')
  const [openId, setOpenId] = useState<string | null>(null)
  const [tab, setTab] = useState<'queue' | 'all'>('queue')
  const [search, setSearch] = useState('')
  const [showEmail, setShowEmail] = useState<string | null>(null)   // draft id
  const [rail, setRail] = useState<'profile' | 'script' | 'faq'>('profile')
  const [note, setNote] = useState('')
  const [outcome, setOutcome] = useState<TmStatus | ''>('')
  const [whenAt, setWhenAt] = useState('')
  const [editScript, setEditScript] = useState(false)
  const [newName, setNewName] = useState<string | null>(null)   // null = not creating
  const [showScript, setShowScript] = useState(false)           // campaign-wide script panel
  const scriptFileRef = useRef<HTMLInputElement>(null)
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null)   // renaming a list row

  // ── Campaigns that can hold a call queue.
  //    Standalone /tm: every Individual Outreach (self-linked) and dedicated TM
  //    campaign. Embedded in a TM campaign's tab: just that one campaign.
  const { data: campaigns } = useQuery({
    queryKey: ['tm-campaigns', user?.id, embedded ? propCampaignId : 'all'],
    enabled: !!user && (!embedded || !!propCampaignId),
    queryFn: async () => {
      let q = supabase.from('campaigns').select('id, name, kind, tm_config').eq('user_id', user!.id)
      q = embedded && propCampaignId ? q.eq('id', propCampaignId) : q.in('kind', ['individual', 'tm'])
      const { data, error: e } = await q.order('name')
      if (e) throw e
      return data as { id: string; name: string; kind: string; tm_config: TmConfig | null }[]
    },
  })
  // Standalone shows a campaign LIST until one is picked (activeId set). Embedded
  // is always locked to its one campaign.
  const activeId = embedded ? (propCampaignId ?? '') : campaignId
  const campaign = campaigns?.find(c => c.id === activeId) ?? (embedded ? campaigns?.[0] : undefined)
  const activeCampaignId = campaign?.id ?? ''
  const config: TmConfig = campaign?.tm_config ?? {}
  const showList = !embedded && !activeCampaignId

  // Module dashboard — dials / meetings / new-to-TM over the last 30 days.
  const { data: tmTrend, isLoading: tmTrendLoading } = useQuery({
    queryKey: ['tm-trend', user?.id],
    enabled: !!user && !embedded,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      const [notes, leadsRows] = await Promise.all([
        supabase.from('tm_notes').select('is_call, outcome, created_at').eq('user_id', user!.id).gte('created_at', since).limit(10000),
        supabase.from('tm_leads').select('created_at').eq('user_id', user!.id).gte('created_at', since).limit(10000),
      ])
      const keys = dayKeys(30)
      const nrows = (notes.data ?? []) as { is_call: boolean; outcome: string | null; created_at: string }[]
      return [
        { label: 'Dials', tone: 'teal' as const, series: bucketCount(nrows.filter(n => n.is_call), keys, r => r.created_at) },
        { label: 'Meetings booked', tone: 'green' as const, series: bucketCount(nrows.filter(n => n.outcome === 'meeting_booked'), keys, r => r.created_at) },
        { label: 'New to TM', tone: 'indigo' as const, series: bucketCount((leadsRows.data ?? []) as { created_at: string }[], keys, r => r.created_at) },
      ]
    },
  })

  // Per-campaign activity split (Today / This week / This month): dials +
  // meetings from tm_notes (mapped to campaign via the lead), new-to-TM from
  // tm_leads.created_at.
  const { data: tmSplit } = useQuery({
    queryKey: ['tm-split', user?.id],
    enabled: !!user && !embedded,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      const [leadsRows, notes] = await Promise.all([
        supabase.from('tm_leads').select('id, campaign_id, created_at').eq('user_id', user!.id).limit(10000),
        supabase.from('tm_notes').select('lead_id, is_call, outcome, created_at').eq('user_id', user!.id).gte('created_at', since).limit(10000),
      ])
      const leads = (leadsRows.data ?? []) as { id: string; campaign_id: string; created_at: string }[]
      const nrows = (notes.data ?? []) as { lead_id: string; is_call: boolean; outcome: string | null; created_at: string }[]
      const leadToCampaign = new Map(leads.map(l => [l.id, l.campaign_id]))
      const nowMs = Date.now(); const todayKey = londonDayKey(new Date())
      const by: Record<string, { dials: Split; meetings: Split; added: Split }> = {}
      const row = (cid: string | undefined) => cid ? (by[cid] ??= { dials: emptySplit(), meetings: emptySplit(), added: emptySplit() }) : null
      for (const l of leads) { const r = row(l.campaign_id); if (r) addToSplit(r.added, l.created_at, 1, nowMs, todayKey) }
      for (const n of nrows) {
        const r = row(leadToCampaign.get(n.lead_id)); if (!r) continue
        if (n.is_call) addToSplit(r.dials, n.created_at, 1, nowMs, todayKey)
        if (n.outcome === 'meeting_booked') addToSplit(r.meetings, n.created_at, 1, nowMs, todayKey)
      }
      return by
    },
  })

  // Per-campaign lead counts for the list view (active contacts only, like the queue).
  const { data: leadStats } = useQuery({
    queryKey: ['tm-campaign-stats', user?.id],
    enabled: !!user && showList,
    queryFn: async () => {
      const { data } = await supabase.from('tm_leads')
        .select('campaign_id, status, callback_at, outreach_contacts!inner(status)')
        .eq('user_id', user!.id).eq('outreach_contacts.status', 'active').limit(5000)
      const m: Record<string, { total: number; queue: number; meetings: number }> = {}
      const now = Date.now()
      for (const l of (data ?? []) as unknown as { campaign_id: string; status: TmStatus; callback_at: string | null }[]) {
        const s = (m[l.campaign_id] ??= { total: 0, queue: 0, meetings: 0 })
        s.total++
        if (l.status === 'to_call' || (l.status === 'callback' && l.callback_at && Date.parse(l.callback_at) <= now)) s.queue++
        if (l.status === 'meeting_booked') s.meetings++
      }
      return m
    },
  })

  // ── Leads. The !inner join is the suppression rule: a contact who replied,
  //    opted out or bounced is no longer `active` and simply isn't returned.
  const { data: leads, isLoading } = useQuery({
    queryKey: ['tm-leads', activeCampaignId],
    enabled: !!activeCampaignId,
    queryFn: async () => {
      const { data, error: e } = await supabase.from('tm_leads')
        .select('*, outreach_contacts!inner(status)')
        .eq('campaign_id', activeCampaignId)
        .eq('outreach_contacts.status', 'active')
        .order('created_at', { ascending: false })
        .limit(2000)
      if (e) throw e
      return (data ?? []) as unknown as TmLead[]
    },
  })

  const open = leads?.find(l => l.id === openId) ?? null

  // ── The job queue ─────────────────────────────────────────────────────────
  // Due now = To Call, or a Callback whose promised time has arrived.
  // Call-backs first (they are promises), then oldest email first.
  const now = Date.now()
  const queue = useMemo(() => {
    return (leads ?? [])
      .filter(l => !CLOSED.includes(l.status))
      .filter(l => l.status === 'to_call' || (l.status === 'callback' && l.callback_at && Date.parse(l.callback_at) <= now))
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === 'callback' ? -1 : 1
        if (a.status === 'callback') return Date.parse(a.callback_at!) - Date.parse(b.callback_at!)
        return Date.parse(a.email_sent_at ?? a.created_at) - Date.parse(b.email_sent_at ?? b.created_at)
      })
  }, [leads, now])

  const upcoming = useMemo(() =>
    (leads ?? []).filter(l => l.status === 'callback' && l.callback_at && Date.parse(l.callback_at) > now)
      .sort((a, b) => Date.parse(a.callback_at!) - Date.parse(b.callback_at!)), [leads, now])

  const term = search.trim().toLowerCase()
  const listed = (tab === 'queue' ? queue : (leads ?? []))
    .filter(l => !term || [l.full_name, l.company, l.email, l.phone].some(v => v?.toLowerCase().includes(term)))

  // ── Counters ──────────────────────────────────────────────────────────────
  const { data: todayCalls } = useQuery({
    queryKey: ['tm-calls-today', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const since = new Date(); since.setHours(0, 0, 0, 0)
      const { count } = await supabase.from('tm_notes').select('id', { count: 'exact', head: true })
        .eq('user_id', user!.id).eq('is_call', true).gte('created_at', since.toISOString())
      return count ?? 0
    },
  })
  const counts = useMemo(() => {
    const c: Record<TmStatus, number> = { to_call: 0, callback: 0, meeting_booked: 0, not_interested: 0, dnc: 0 }
    for (const l of leads ?? []) c[l.status]++
    return c
  }, [leads])

  // ── Work log for the open lead ────────────────────────────────────────────
  const { data: notes } = useQuery({
    queryKey: ['tm-notes', openId],
    enabled: !!openId,
    queryFn: async () => {
      const { data, error: e } = await supabase.from('tm_notes')
        .select('*').eq('lead_id', openId!).order('created_at', { ascending: false })
      if (e) throw e
      return data as TmNote[]
    },
  })

  // ── The emails this person was actually sent ──────────────────────────────
  const { data: emails } = useQuery({
    queryKey: ['tm-emails', open?.outreach_contact_id],
    enabled: !!open,
    queryFn: async () => {
      const { data, error: e } = await supabase.from('outreach_drafts')
        .select('id, touch_no, subject, body, status, sent_at, scheduled_for')
        .eq('contact_id', open!.outreach_contact_id).order('touch_no')
      if (e) throw e
      return data as SentEmail[]
    },
  })

  // Merge context for the preview: the contact's own fields + the SOURCE outreach
  // campaign's snippets and signature (so {{tags}} render as they were sent).
  const { data: mergeCtx } = useQuery({
    queryKey: ['tm-merge-ctx', open?.outreach_contact_id],
    enabled: !!open,
    queryFn: async () => {
      const { data: oc } = await supabase.from('outreach_contacts')
        .select('first_name, last_name, firm, campaign_id').eq('id', open!.outreach_contact_id).single()
      let cfg: { signature?: string; presets?: Preset[] } = {}
      if (oc?.campaign_id) {
        const { data: camp } = await supabase.from('campaigns').select('outreach_config').eq('id', oc.campaign_id).single()
        cfg = (camp?.outreach_config ?? {}) as { signature?: string; presets?: Preset[] }
      }
      return {
        contact: oc as MergeContact | null,
        presets: cfg.presets ?? [],
        signature: cfg.signature ?? '',
      }
    },
  })

  // ── Mutations ─────────────────────────────────────────────────────────────
  /** Log the call: append the note, move the status, bump the counters. */
  const logCall = useMutation({
    mutationFn: async () => {
      if (!open) throw new Error('No lead open')
      if (!outcome) throw new Error('Choose an outcome — the entry cannot be saved without one.')
      if (outcome === 'callback' && !whenAt) throw new Error('A callback needs the date and time you promised.')
      if (outcome === 'meeting_booked' && !whenAt) throw new Error('A booked meeting needs its date and time.')

      const at = whenAt ? new Date(whenAt).toISOString() : null
      const { error: e1 } = await supabase.from('tm_notes').insert({
        user_id: user!.id, lead_id: open.id, body: note.trim(), outcome, is_call: true,
      })
      if (e1) throw e1

      const patch: Record<string, unknown> = {
        status: outcome,
        last_call_at: new Date().toISOString(),
        call_count: open.call_count + 1,
        callback_at: outcome === 'callback' ? at : null,
        meeting_at: outcome === 'meeting_booked' ? at : open.meeting_at,
      }
      const { error: e2 } = await supabase.from('tm_leads').update(patch).eq('id', open.id)
      if (e2) throw e2
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => {
      setNote(''); setOutcome(''); setWhenAt(''); setError('')
      qc.invalidateQueries({ queryKey: ['tm-leads', activeCampaignId] })
      qc.invalidateQueries({ queryKey: ['tm-notes', openId] })
      qc.invalidateQueries({ queryKey: ['tm-calls-today', user?.id] })
    },
  })

  /** A plain note — no dial, so it never inflates the call count. */
  const addNote = useMutation({
    mutationFn: async () => {
      if (!open || !note.trim()) throw new Error('Write something first.')
      const { error: e } = await supabase.from('tm_notes').insert({
        user_id: user!.id, lead_id: open.id, body: note.trim(), outcome: null, is_call: false,
      })
      if (e) throw e
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { setNote(''); setError(''); qc.invalidateQueries({ queryKey: ['tm-notes', openId] }) },
  })

  const enrichOne = useMutation({
    mutationFn: async (leadId: string) => {
      const r = await authedFetch('/api/tm/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ leadId }),
      })
      const j = await r.json() as { ok: boolean; message: string }
      if (!r.ok) throw new Error(j.message || 'Enrichment failed')
      return j
    },
    onError: e => setError(describeError(e)),
    onSuccess: j => { setInfo(j.message); qc.invalidateQueries({ queryKey: ['tm-leads', activeCampaignId] }) },
  })

  const enrichPending = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/tm/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: activeCampaignId, limit: 10 }),
      })
      const j = await r.json() as { ok: boolean; message: string }
      if (!r.ok) throw new Error(j.message || 'Enrichment failed')
      return j
    },
    onError: e => setError(describeError(e)),
    onSuccess: j => { setInfo(j.message); qc.invalidateQueries({ queryKey: ['tm-leads', activeCampaignId] }) },
  })

  const saveConfig = useMutation({
    mutationFn: async (patch: TmConfig) => {
      const { error: e } = await supabase.from('campaigns')
        .update({ tm_config: { ...config, ...patch } }).eq('id', activeCampaignId)
      if (e) throw e
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tm-campaigns', user?.id] }),
  })

  // Create a dedicated TM campaign right here — no need to detour via Campaigns.
  const createCampaign = useMutation({
    mutationFn: async (name: string) => {
      const { data, error: e } = await supabase.from('campaigns').insert({
        user_id: user!.id, name, description: '', colour: '#0d9488', icon: '📞',
        audience: 'director', kind: 'tm', sending_profile_id: null,
      }).select('id').single()
      if (e) throw e
      return data.id as string
    },
    onError: e => setError(describeError(e)),
    onSuccess: async (id) => {
      setNewName(null); setError('')
      await qc.invalidateQueries({ queryKey: ['tm-campaigns', user?.id] })
      setCampaignId(id); setOpenId(null)
    },
  })

  // Rename / delete the selected TM campaign (only its own kind — never an
  // individual outreach campaign that merely shows here self-linked).
  const renameCampaign = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error: e } = await supabase.from('campaigns').update({ name }).eq('id', id).eq('user_id', user!.id)
      if (e) throw e
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { setEditing(null); setError(''); qc.invalidateQueries({ queryKey: ['tm-campaigns', user?.id] }) },
  })
  const deleteCampaign = useMutation({
    mutationFn: async (id: string) => {
      const { error: e } = await supabase.from('campaigns').delete().eq('id', id).eq('user_id', user!.id)
      if (e) throw e
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { setOpenId(null); setCampaignId(''); setError(''); qc.invalidateQueries({ queryKey: ['tm-campaigns', user?.id] }); qc.invalidateQueries({ queryKey: ['tm-campaign-stats', user?.id] }) },
  })

  const pendingEnrichment = (leads ?? []).filter(l => l.enrichment_status === 'pending' || l.enrichment_status === 'failed').length

  // ── Render ────────────────────────────────────────────────────────────────
  if (!campaigns?.length) {
    return (
      <div className={embedded ? '' : 'p-6'}>
        <EmptyState icon={Phone} title="No call queues yet"
          description="Create a dedicated TM campaign to call into, or feed one from an Individual Outreach campaign with “Copy Data to TM”."
          action={!embedded ? (
            newName === null ? (
              <button onClick={() => setNewName('')} className="btn-primary"><Plus className="w-4 h-4" /> New TM campaign</button>
            ) : (
              <div className="flex items-center gap-2">
                <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) createCampaign.mutate(newName.trim()); if (e.key === 'Escape') setNewName(null) }}
                  placeholder="Campaign name" className="input py-1.5 text-sm" />
                <button onClick={() => newName.trim() && createCampaign.mutate(newName.trim())} disabled={createCampaign.isPending || !newName.trim()} className="btn-primary text-sm disabled:opacity-50">{createCampaign.isPending ? 'Creating…' : 'Create'}</button>
                <button onClick={() => setNewName(null)} className="btn-secondary text-sm">Cancel</button>
              </div>
            )
          ) : undefined} />
      </div>
    )
  }

  // ── CAMPAIGN LIST (standalone landing) ────────────────────────────────────
  // Clicking "TM — Telemarketing" lands here: pick a campaign to work its queue.
  if (showList) {
    const rows = campaigns.filter(c => c.kind === 'tm' || (leadStats?.[c.id]?.total ?? 0) > 0)
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Phone className="w-5 h-5 text-teal-600" /> TM — Telemarketing
          </h1>
          {newName === null ? (
            <button onClick={() => setNewName('')} className="btn-primary text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> New TM campaign</button>
          ) : (
            <span className="flex items-center gap-1">
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) createCampaign.mutate(newName.trim()); if (e.key === 'Escape') setNewName(null) }}
                placeholder="Campaign name" className="input py-1 text-sm w-52" />
              <button onClick={() => newName.trim() && createCampaign.mutate(newName.trim())} disabled={createCampaign.isPending || !newName.trim()} className="btn-primary text-xs disabled:opacity-50">{createCampaign.isPending ? '…' : 'Create'}</button>
              <button onClick={() => setNewName(null)} className="btn-secondary text-xs">✕</button>
            </span>
          )}
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}
        {info && <div className="rounded-md bg-teal-50 border border-teal-200 px-3 py-2 text-xs text-teal-800">{info}</div>}

        <TrendStrip metrics={tmTrend} loading={tmTrendLoading} />

        <p className="text-sm text-gray-500">Pick a campaign to work its call queue.</p>

        {!rows.length ? (
          <EmptyState icon={Phone} title="No call queues yet"
            description="Create a TM campaign above, or feed one from an Individual Outreach campaign with “Copy Data to TM”." />
        ) : (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {rows.map(c => {
              const st = leadStats?.[c.id] ?? { total: 0, queue: 0, meetings: 0 }
              const ed = editing && editing.id === c.id ? editing : null
              return (
                <div key={c.id} className="flex items-center gap-3 px-3 py-3 hover:bg-gray-50">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Phone className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    {ed ? (
                      <span className="flex items-center gap-1">
                        <input autoFocus value={ed.name} onChange={e => setEditing({ id: c.id, name: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter' && ed.name.trim()) renameCampaign.mutate({ id: c.id, name: ed.name.trim() }); if (e.key === 'Escape') setEditing(null) }}
                          className="input py-1 text-sm w-56" />
                        <button onClick={() => ed.name.trim() && renameCampaign.mutate({ id: c.id, name: ed.name.trim() })} disabled={renameCampaign.isPending || !ed.name.trim()} className="btn-primary text-xs disabled:opacity-50"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setEditing(null)} className="btn-secondary text-xs"><X className="w-3.5 h-3.5" /></button>
                      </span>
                    ) : (
                      <button onClick={() => { setCampaignId(c.id); setOpenId(null) }} className="text-left w-full">
                        <p className="font-medium text-gray-900 text-sm truncate flex items-center gap-2">
                          {c.name}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium shrink-0 ${c.kind === 'tm' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-teal-50 text-teal-700 border-teal-200'}`}>{c.kind === 'tm' ? 'TM' : 'Individual'}</span>
                        </p>
                        <p className="text-[11px] text-gray-500 truncate">{st.queue} in queue · {st.total} total{st.meetings ? ` · ${st.meetings} meeting${st.meetings === 1 ? '' : 's'}` : ''} <span className="text-gray-400">(now)</span></p>
                        <span className="block mt-1.5 max-w-xs">
                          <RowSplit metrics={[
                            { label: 'Dials', tone: 'teal', split: tmSplit?.[c.id]?.dials ?? emptySplit() },
                            { label: 'Meetings', tone: 'green', split: tmSplit?.[c.id]?.meetings ?? emptySplit() },
                            { label: 'New to TM', tone: 'indigo', split: tmSplit?.[c.id]?.added ?? emptySplit() },
                          ]} />
                        </span>
                      </button>
                    )}
                  </div>
                  {!ed && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      {c.kind === 'tm' && (
                        <>
                          <button title="Rename" onClick={() => setEditing({ id: c.id, name: c.name })} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                          <button title="Delete" onClick={() => { if (confirm(`Delete "${c.name}"? This permanently removes the campaign and its entire call queue — every lead, call log and note in it.`)) deleteCampaign.mutate(c.id) }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                      <button onClick={() => { setCampaignId(c.id); setOpenId(null) }} className="p-1.5 text-gray-300 hover:text-gray-600"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? 'space-y-4' : 'p-4 sm:p-6 space-y-4'}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {!embedded && (
            <button onClick={() => { setCampaignId(''); setOpenId(null) }} className="btn-secondary text-xs flex items-center gap-1 shrink-0">
              <ChevronLeft className="w-3.5 h-3.5" /> All campaigns
            </button>
          )}
          {!embedded && (
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2 min-w-0">
              <Phone className="w-5 h-5 text-teal-600 shrink-0" /> <span className="truncate">{campaign?.name}</span>
            </h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pendingEnrichment > 0 && (
            <button onClick={() => enrichPending.mutate()} disabled={enrichPending.isPending}
              className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" />
              {enrichPending.isPending ? 'Researching…' : `Enrich ${Math.min(10, pendingEnrichment)} of ${pendingEnrichment}`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}
      {info && <div className="rounded-md bg-teal-50 border border-teal-200 px-3 py-2 text-xs text-teal-800">{info}</div>}

      {/* Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Dials today" value={todayCalls ?? 0} icon={PhoneCall} tone="teal" />
        <Stat label="In queue now" value={queue.length} icon={Clock} tone="teal" />
        <Stat label="Call-backs ahead" value={upcoming.length} icon={CalendarClock} tone="amber" />
        <Stat label="Meetings booked" value={counts.meeting_booked} icon={CalendarCheck} tone="green" />
        <Stat label="Closed out" value={counts.not_interested + counts.dnc} icon={Ban} tone="gray" />
      </div>

      {open ? (
        // ── LEAD DETAIL ───────────────────────────────────────────────────────
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* CRM record */}
          <div className="lg:col-span-2 space-y-3">
            <button onClick={() => setOpenId(null)} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to queue
            </button>

            {/* Header card — everything the caller needs before dialling */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{open.full_name || open.email}</h2>
                  <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-0.5">
                    <Building2 className="w-3.5 h-3.5 text-gray-400" />{open.company || '—'}
                    {open.website && <a href={`https://${open.website}`} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline">{open.website}</a>}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />{open.email}
                  </p>
                </div>
                <span className={`text-[11px] px-2 py-1 rounded-full border ${STATUS[open.status].pill}`}>{STATUS[open.status].label}</span>
              </div>

              {/* Phone */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 flex-wrap">
                {open.phone ? (
                  <>
                    <a href={`tel:${open.phone.replace(/\s/g, '')}`} className="text-lg font-semibold text-teal-700 tabular-nums hover:underline">{open.phone}</a>
                    {open.phone_confidence && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                        open.phone_confidence === 'high' ? 'bg-green-50 text-green-700 border-green-200'
                        : open.phone_confidence === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{open.phone_confidence} confidence</span>
                    )}
                  </>
                ) : (
                  <span className="text-sm text-gray-400">
                    {open.enrichment_status === 'no_data' ? 'No number found — check their website' : 'Not researched yet'}
                  </span>
                )}
                <button onClick={() => enrichOne.mutate(open.id)} disabled={enrichOne.isPending}
                  className="btn-secondary text-[11px] flex items-center gap-1 disabled:opacity-50">
                  <RefreshCw className={`w-3 h-3 ${enrichOne.isPending ? 'animate-spin' : ''}`} />
                  {open.phone ? 'Re-check' : 'Find number'}
                </button>
              </div>
              {open.phone_source && <p className="mt-1 text-[10px] text-gray-400">Seen at: {open.phone_source}</p>}

              {/* Email context */}
              <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <Field label="Last email sent" value={open.email_sent_at ? `${ukDate(open.email_sent_at)} (${daysAgo(open.email_sent_at)})` : '—'} />
                <Field label="Touch" value={`Touch ${open.source_touch_no}`} />
                <Field label="Next email" value={open.next_email_at ? ukDate(open.next_email_at) : 'none scheduled'} />
                <Field label="Calls made" value={String(open.call_count)} />
              </div>
              {open.status === 'callback' && open.callback_at && (
                <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Call back {ukStamp(open.callback_at)}
                </p>
              )}
              {open.status === 'meeting_booked' && open.meeting_at && (
                <p className="mt-2 text-xs text-green-700 flex items-center gap-1.5">
                  <CalendarCheck className="w-3.5 h-3.5" /> Meeting {ukStamp(open.meeting_at)}
                </p>
              )}
            </div>

            {/* The emails they were actually sent */}
            <div className="border border-gray-200 rounded-lg">
              <div className="px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-1.5 border-b border-gray-100">
                <Mail className="w-4 h-4" /> Emails sent
              </div>
              <div className="divide-y divide-gray-100">
                {(emails ?? []).length === 0 && <p className="px-3 py-3 text-xs text-gray-400">No emails on record.</p>}
                {(emails ?? []).map(em => (
                  <div key={em.id}>
                    <button onClick={() => setShowEmail(showEmail === em.id ? null : em.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${
                        em.status === 'sent' ? 'bg-green-50 text-green-700 border-green-200'
                        : em.status === 'scheduled' ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200'}`}>T{em.touch_no}</span>
                      <span className="text-xs text-gray-800 truncate flex-1">{fillTags(em.subject, mergeCtx?.contact, mergeCtx?.presets ?? [], false) || '(no subject)'}</span>
                      <span className="text-[11px] text-gray-400 shrink-0">
                        {em.sent_at ? ukDate(em.sent_at) : em.scheduled_for ? `→ ${ukDate(em.scheduled_for)}` : em.status}
                      </span>
                    </button>
                    {showEmail === em.id && (
                      <div className="px-3 pb-3">
                        <div className="rounded border border-gray-200 bg-white p-3 text-sm max-h-80 overflow-auto leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: renderSentPreview(em.body, mergeCtx?.contact, mergeCtx?.presets ?? [], mergeCtx?.signature ?? '') || '<p style="color:#9ca3af">Empty</p>' }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Log the call */}
            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><PhoneCall className="w-4 h-4" /> Log this call</p>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="What happened — one line is enough."
                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
              <div className="flex items-end gap-2 flex-wrap">
                <label className="text-xs">
                  <span className="text-gray-500 block mb-0.5">Outcome *</span>
                  <select value={outcome} onChange={e => { setOutcome(e.target.value as TmStatus); setWhenAt('') }}
                    className="input py-1 text-sm">
                    <option value="">— choose —</option>
                    {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
                  </select>
                </label>
                {(outcome === 'callback' || outcome === 'meeting_booked') && (
                  <label className="text-xs">
                    <span className="text-gray-500 block mb-0.5">{outcome === 'callback' ? 'Call back at *' : 'Meeting at *'}</span>
                    <input type="datetime-local" value={whenAt} onChange={e => setWhenAt(e.target.value)} className="input py-1 text-sm" />
                  </label>
                )}
                <button onClick={() => logCall.mutate()} disabled={logCall.isPending || !outcome}
                  className="btn-primary text-xs disabled:opacity-50">
                  {logCall.isPending ? 'Saving…' : 'Log call'}
                </button>
                <button onClick={() => addNote.mutate()} disabled={addNote.isPending || !note.trim()}
                  className="btn-secondary text-xs disabled:opacity-50" title="Save a note without counting it as a dial">
                  Note only
                </button>
              </div>
              {outcome === 'dnc' && (
                <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  This removes them from every future queue. Only you can undo it.
                </p>
              )}
            </div>

            {/* Work log */}
            <div className="border border-gray-200 rounded-lg">
              <div className="px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-1.5 border-b border-gray-100">
                <StickyNote className="w-4 h-4" /> Work log
              </div>
              <div className="divide-y divide-gray-100 max-h-96 overflow-auto">
                {(notes ?? []).length === 0 && <p className="px-3 py-3 text-xs text-gray-400">Nothing logged yet.</p>}
                {(notes ?? []).map(n => (
                  <div key={n.id} className="px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-gray-400 tabular-nums">{ukStamp(n.created_at)}</span>
                      {n.outcome && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS[n.outcome].pill}`}>{STATUS[n.outcome].label}</span>}
                      {!n.is_call && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-100 text-gray-500 border-gray-200">note</span>}
                    </div>
                    {n.body && <p className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">{n.body}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Caller's rail */}
          <div className="space-y-3">
            <div className="border border-gray-200 rounded-lg">
              <div className="flex border-b border-gray-100 text-xs">
                {([['profile', 'Profile', Sparkles], ['script', 'Script', FileText], ['faq', 'FAQ', HelpCircle]] as const).map(([k, label, Icon]) => (
                  <button key={k} onClick={() => setRail(k)}
                    className={`flex-1 px-2 py-2 flex items-center justify-center gap-1 ${rail === k ? 'text-teal-700 border-b-2 border-teal-600 font-medium' : 'text-gray-500'}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>

              {rail === 'profile' && (
                <div className="p-3 space-y-2">
                  {open.company_summary
                    ? <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{open.company_summary}</p>
                    : <p className="text-xs text-gray-400">
                        {open.enrichment_status === 'running' ? 'Researching…' : 'No profile yet — use “Find number” above to research this company.'}
                      </p>}
                  {open.company_summary && (
                    <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-100">
                      Written by AI from public sources. Check anything you plan to say as fact.
                    </p>
                  )}
                </div>
              )}

              {rail === 'script' && (
                <div className="p-3 space-y-2">
                  {editScript ? (
                    <>
                      <textarea defaultValue={config.script ?? ''} rows={16} id="tm-script"
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs font-mono" />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditScript(false)} className="btn-secondary text-xs">Cancel</button>
                        <button className="btn-primary text-xs" onClick={() => {
                          const el = document.getElementById('tm-script') as HTMLTextAreaElement | null
                          saveConfig.mutate({ script: el?.value ?? '' }); setEditScript(false)
                        }}>Save script</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {config.script
                        ? <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{scriptFor(config.script, open)}</p>
                        : <p className="text-xs text-gray-400">No script yet. Write the first-call script once and it shows on every lead in this campaign.</p>}
                      <button onClick={() => setEditScript(true)} className="btn-secondary text-xs">{config.script ? 'Edit script' : 'Write script'}</button>
                    </>
                  )}
                </div>
              )}

              {rail === 'faq' && (
                <div className="p-3 space-y-2">
                  {(config.faq ?? []).map((f, i) => (
                    <details key={i} className="border border-gray-200 rounded p-2">
                      <summary className="text-xs font-medium text-gray-800 cursor-pointer">{f.q}</summary>
                      <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{f.a}</p>
                      <button onClick={() => saveConfig.mutate({ faq: (config.faq ?? []).filter((_, j) => j !== i) })}
                        className="text-[10px] text-gray-400 hover:text-red-600 mt-1">delete</button>
                    </details>
                  ))}
                  <div className="border border-dashed border-gray-300 rounded p-2 space-y-1">
                    <input id="tm-faq-q" placeholder="Question they ask…" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
                    <textarea id="tm-faq-a" placeholder="What you say back…" rows={3} className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
                    <button className="btn-secondary text-xs" onClick={() => {
                      const q = (document.getElementById('tm-faq-q') as HTMLInputElement | null)?.value.trim() ?? ''
                      const a = (document.getElementById('tm-faq-a') as HTMLTextAreaElement | null)?.value.trim() ?? ''
                      if (!q || !a) { setError('A FAQ needs both a question and an answer.'); return }
                      saveConfig.mutate({ faq: [...(config.faq ?? []), { q, a }] })
                      ;(document.getElementById('tm-faq-q') as HTMLInputElement).value = ''
                      ;(document.getElementById('tm-faq-a') as HTMLTextAreaElement).value = ''
                    }}>Add FAQ</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // ── QUEUE ─────────────────────────────────────────────────────────────
        <div className="space-y-3">
          {/* Campaign-wide call script + FAQ — shared by every lead in this campaign */}
          <div className="border border-gray-200 rounded-lg">
            <button onClick={() => setShowScript(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700">
              <span className="flex items-center gap-1.5">
                <FileText className="w-4 h-4" /> Call script & FAQ
                <span className="text-[11px] font-normal text-gray-400">— shared by everyone in {campaign?.name}{config.script ? '' : ' · none set yet'}</span>
              </span>
              {showScript ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showScript && (
              <div className="px-3 pb-3 space-y-4">
                {/* Script */}
                <div>
                  <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                    <p className="text-xs font-medium text-gray-600">First-call script</p>
                    <button onClick={() => scriptFileRef.current?.click()} className="btn-secondary text-[11px] flex items-center gap-1"><Upload className="w-3 h-3" /> Upload .txt</button>
                    <input ref={scriptFileRef} type="file" accept=".txt,text/plain,.md" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) f.text().then(t => { saveConfig.mutate({ script: t }); setInfo('Script uploaded and saved for the whole campaign.') }).catch(() => setError('Could not read that file.')); e.target.value = '' }} />
                  </div>
                  <textarea key={`camp-script-${activeCampaignId}`} id="tm-campaign-script" defaultValue={config.script ?? ''} rows={10}
                    placeholder="Write the opening script once — it shows on every lead in this campaign. Paste it here, or upload a .txt above."
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs font-mono" />
                  <div className="flex items-center justify-between mt-1 gap-2 flex-wrap">
                    <p className="text-[10px] text-gray-400">Tokens fill per lead: {'{{firstName}}'}, {'{{company}}'}, {'{{emailDay}}'}, {'{{emailDate}}'}.</p>
                    <button className="btn-primary text-xs" onClick={() => {
                      const el = document.getElementById('tm-campaign-script') as HTMLTextAreaElement | null
                      saveConfig.mutate({ script: el?.value ?? '' }); setInfo('Script saved for the whole campaign.')
                    }}>Save script</button>
                  </div>
                </div>

                {/* FAQ */}
                <div className="space-y-2 pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-600">FAQ — answers to what they ask</p>
                  {(config.faq ?? []).map((f, i) => (
                    <details key={i} className="border border-gray-200 rounded p-2">
                      <summary className="text-xs font-medium text-gray-800 cursor-pointer">{f.q}</summary>
                      <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{f.a}</p>
                      <button onClick={() => saveConfig.mutate({ faq: (config.faq ?? []).filter((_, j) => j !== i) })}
                        className="text-[10px] text-gray-400 hover:text-red-600 mt-1">delete</button>
                    </details>
                  ))}
                  <div className="border border-dashed border-gray-300 rounded p-2 space-y-1">
                    <input id="tm-cfaq-q" placeholder="Question they ask…" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
                    <textarea id="tm-cfaq-a" placeholder="What you say back…" rows={2} className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
                    <button className="btn-secondary text-xs" onClick={() => {
                      const q = (document.getElementById('tm-cfaq-q') as HTMLInputElement | null)?.value.trim() ?? ''
                      const a = (document.getElementById('tm-cfaq-a') as HTMLTextAreaElement | null)?.value.trim() ?? ''
                      if (!q || !a) { setError('A FAQ needs both a question and an answer.'); return }
                      saveConfig.mutate({ faq: [...(config.faq ?? []), { q, a }] })
                      ;(document.getElementById('tm-cfaq-q') as HTMLInputElement).value = ''
                      ;(document.getElementById('tm-cfaq-a') as HTMLTextAreaElement).value = ''
                    }}>Add FAQ</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
              <button onClick={() => setTab('queue')} className={`px-3 py-1.5 ${tab === 'queue' ? 'bg-teal-600 text-white' : 'text-gray-600'}`}>Queue ({queue.length})</button>
              <button onClick={() => setTab('all')} className={`px-3 py-1.5 ${tab === 'all' ? 'bg-teal-600 text-white' : 'text-gray-600'}`}>All ({leads?.length ?? 0})</button>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, firm, number…"
                className="input py-1 pl-7 text-xs w-56" />
            </div>
          </div>

          {isLoading ? <div className="py-10 flex justify-center"><Spinner /></div>
            : !listed.length ? (
              <EmptyState icon={Phone}
                title={tab === 'queue' ? 'Queue is clear' : 'Nobody in TM yet'}
                description={tab === 'queue'
                  ? 'Nothing due right now. Call-backs reappear here on the date you promised.'
                  : 'Open the outreach campaign, and send Touch-1-sent contacts here with the TM button.'} />
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {listed.map(l => (
                  <button key={l.id} onClick={() => { setOpenId(l.id); setRail('profile'); setNote(''); setOutcome(''); setWhenAt(''); setError('') }}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {l.full_name || l.email}
                        {l.company && <span className="text-gray-400 font-normal"> · {l.company}</span>}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {l.phone
                          ? <span className="tabular-nums text-teal-700">{l.phone}</span>
                          : <span className="text-gray-400">no number yet</span>}
                        {l.email_sent_at && <> · T{l.source_touch_no} sent {ukDate(l.email_sent_at)} ({daysAgo(l.email_sent_at)})</>}
                        {l.call_count > 0 && <> · {l.call_count} call{l.call_count === 1 ? '' : 's'}</>}
                      </p>
                    </div>
                    {l.status === 'callback' && l.callback_at && (
                      <span className="text-[11px] text-amber-700 shrink-0 tabular-nums">{ukStamp(l.callback_at)}</span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${STATUS[l.status].pill}`}>{STATUS[l.status].label}</span>
                  </button>
                ))}
              </div>
            )}

          {/* Promised call-backs still ahead of us */}
          {tab === 'queue' && upcoming.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-lg">
              <p className="px-3 py-2 text-xs font-medium text-amber-800 flex items-center gap-1.5 border-b border-amber-100">
                <CalendarClock className="w-3.5 h-3.5" /> Call-backs ahead ({upcoming.length})
              </p>
              <div className="divide-y divide-amber-100">
                {upcoming.slice(0, 10).map(l => (
                  <button key={l.id} onClick={() => setOpenId(l.id)} className="w-full text-left px-3 py-1.5 hover:bg-amber-50 flex items-center gap-2">
                    <span className="text-xs text-gray-800 truncate flex-1">{l.full_name || l.email}{l.company ? ` · ${l.company}` : ''}</span>
                    <span className="text-[11px] text-amber-700 tabular-nums shrink-0">{ukStamp(l.callback_at!)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Merge the caller's tokens into the saved script. */
function scriptFor(script: string, l: TmLead): string {
  const first = (l.full_name ?? '').split(' ')[0] || 'there'
  return script
    .replace(/\{\{\s*firstName\s*\}\}/gi, first)
    .replace(/\{\{\s*fullName\s*\}\}/gi, l.full_name ?? 'them')
    .replace(/\{\{\s*(company|firm)\s*\}\}/gi, l.company ?? 'the firm')
    .replace(/\{\{\s*emailDay\s*\}\}/gi, l.email_sent_at
      ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'long' }).format(new Date(l.email_sent_at))
      : 'recently')
    .replace(/\{\{\s*emailDate\s*\}\}/gi, l.email_sent_at ? ukDate(l.email_sent_at) : 'recently')
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-gray-800">{value}</p>
    </div>
  )
}

function Stat({ label, value, icon: Icon, tone }: {
  label: string; value: number; icon: typeof Phone; tone: 'teal' | 'amber' | 'green' | 'gray'
}) {
  const tones = {
    teal: 'text-teal-700 bg-teal-50 border-teal-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    green: 'text-green-700 bg-green-50 border-green-200',
    gray: 'text-gray-600 bg-gray-50 border-gray-200',
  }[tone]
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones}`}>
      <p className="text-[10px] uppercase tracking-wide flex items-center gap-1 opacity-80"><Icon className="w-3 h-3" />{label}</p>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}
